import { Reducer } from 'redux'
import {
  AssistantBotKey,
  AssistantBotState,
  AssistantBotActionTypes,
  TestsAssistantBotProperties
} from './types'
import {
  AssistantBotAssistantType,
  CodeHelperMessages,
  VisualizerMessages
} from 'common/types/assistantBot'
import { PYPLAN_ASSISTANT } from 'pages/app/components/constants'

export const initialState: AssistantBotState = {
  general: {
    assistantBotType: AssistantBotAssistantType.pyplan,
    assistantBotTypeQuestionAnswered: false,
    messages: [],
    excludedThreadIds: []
  },
  tests: {
    assistantBotType: AssistantBotAssistantType.application,
    assistantBotTypeQuestionAnswered: false,
    messages: [],
    excludedThreadIds: []
  }
}

const reducer: Reducer<AssistantBotState> = (
  state: AssistantBotState = initialState,
  action: { type: AssistantBotActionTypes; payload?: any }
) => {
  const assistantBotKey: AssistantBotKey =
    action?.payload?.assistantBotKey || AssistantBotKey.general
  const assistantBotKeyState = state[assistantBotKey]
  switch (action.type) {
    case AssistantBotActionTypes.APPEND_MESSAGE: {
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          messages: [...assistantBotKeyState.messages, action.payload.message]
        }
      }
    }
    case AssistantBotActionTypes.UPDATE_MESSAGES: {
      return {
        ...state,
        [assistantBotKey]: { ...assistantBotKeyState, messages: action.payload.messages }
      }
    }

    case AssistantBotActionTypes.SHOW: {
      return {
        ...state,
        show: action.payload.show
      }
    }
    case AssistantBotActionTypes.CLEAR_MESSAGE: {
      return { ...state, [assistantBotKey]: { ...assistantBotKeyState, messages: [] } }
    }
    case AssistantBotActionTypes.SET_POSITION: {
      return {
        ...state,
        [assistantBotKey]: { ...assistantBotKeyState, position: action.payload.position }
      }
    }
    case AssistantBotActionTypes.SET_SIZE: {
      return { ...state, [assistantBotKey]: { ...assistantBotKeyState, size: action.payload.size } }
    }
    case AssistantBotActionTypes.LISTEN_BOT_MESSAGES: {
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          listenBotMessages: action.payload.listenBotMessages
        }
      }
    }
    case AssistantBotActionTypes.SET_EXCLUDED_THREAD_ID: {
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          excludedThreadIds: [
            ...assistantBotKeyState.excludedThreadIds,
            action.payload.excludedThreadId
          ]
        }
      }
    }
    case AssistantBotActionTypes.CLEAR_EXCLUDED_THREAD_ID: {
      return { ...state, [assistantBotKey]: { ...assistantBotKeyState, excludedThreadIds: [] } }
    }
    case AssistantBotActionTypes.SET_ASSISTANT_BOT_TYPE: {
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          assistantBotType: action.payload.assistantBotType,
          assistantBotId: action.payload.assistantBotId,
          assistantBotEngineType: action.payload.assistantBotEngineType
        }
      }
    }
    case AssistantBotActionTypes.SET_ASSISTANT_BOT_TYPE_QUESTION_ANSWERED: {
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          assistantBotTypeQuestionAnswered: action.payload.answered
        }
      }
    }
    case AssistantBotActionTypes.SET_APPLICATION_ASSISTANT_BOT: {
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          startApplicationAssistantBot: action.payload
        }
      }
    }
    case AssistantBotActionTypes.SET_ASSISTANT_TYPE_RESPONSE:
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          assistantTypeResponse: action.payload.action
        }
      }
    case AssistantBotActionTypes.LOADING_PYPLAN_CODE_ASSISTANT:
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          loadingPyplanCodeAssistant: action.payload.value
        }
      }
    case AssistantBotActionTypes.SET_NODE_BOT_MESSAGES:
      const nodeId = Object.keys(action.payload.value as CodeHelperMessages)[0]
      const nodeMessages = { ...state[AssistantBotKey.general].nodeMessages }
      nodeMessages[nodeId] = action.payload.value[nodeId]
      if (Object.keys(nodeMessages).length > 10) {
        delete nodeMessages[Object.keys(nodeMessages)[0]]
      }
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          nodeMessages: {
            ...nodeMessages
          }
        }
      }
    case AssistantBotActionTypes.SET_INTERFACE_MESSAGES:
      const interfaceComponentId = Object.keys(action.payload.value as VisualizerMessages)[0]
      const interfaceMessages = { ...state[AssistantBotKey.general].interfaceMessages }
      interfaceMessages[interfaceComponentId] = action.payload.value[interfaceComponentId]
      if (Object.keys(interfaceMessages).length > 10) {
        delete interfaceMessages[Object.keys(interfaceMessages)[0]]
      }
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          interfaceMessages: {
            ...interfaceMessages
          }
        }
      }
    case AssistantBotActionTypes.SET_AUTO_RUN_NODE:
      return {
        ...state,
        [assistantBotKey]: {
          ...assistantBotKeyState,
          autoRunNode: action.payload.value
        }
      }
    case AssistantBotActionTypes.SET_AVAILABLE_COMPANY_ASSISTANT:
      return {
        ...state,
        availableCompanyAssistant: action.payload.value
      }
    case AssistantBotActionTypes.SET_OPEN_ASSISTANTS_TESTS:
      return {
        ...state,
        [AssistantBotKey.tests]: {
          ...(assistantBotKeyState as TestsAssistantBotProperties),
          show: action.payload.value
        }
      }
    case AssistantBotActionTypes.SET_COMPANY_APPLICATION_ASSISTANT_REFRESH:
      return {
        ...state,
        companyApplicationAssistantRefresh: action.payload
      }
    case AssistantBotActionTypes.RESET_BOT_INFO: {
      return {
        ...state,
        general: {
          ...initialState.general,
          assistantBotType: AssistantBotAssistantType.pyplan,
          assistantBotId: 'pyplan_assistant'
        },
        tests: {
          ...initialState.tests,
          assistantBotType: state.tests.assistantBotType
        },
        startApplicationAssistantBot: undefined,
        availableCompanyAssistant: [PYPLAN_ASSISTANT],
        companyApplicationAssistantRefresh: false
      }
    }
    default: {
      return state
    }
  }
}

// Instead of using default export, we use named exports. That way we can group these exports
// inside the `index.js` folder.
export { reducer as assistantBotReducer }
